<template>
  <OurPage
    :back-route="backRoute"
    class="access-groups promotions-edit"
    :title="$t('title.promotions.edit')"
  >
    <template #header-right>
      <div v-if="statusTag" class="flex max-w-[220px] flex-col items-end gap-1 text-right">
        <OurTag :text="statusTag.text" :color="statusTag.color" />
        <div v-if="createdAtFormatted || creatorName" class="text-xs text-gray-500">
          <div v-if="createdAtFormatted">{{ createdAtFormatted }}</div>
          <div v-if="creatorName">{{ creatorName }}</div>
        </div>
        <span v-if="completedEarlyAtFormatted" class="text-sm text-gray-600">
          {{ $t("promotions.completedEarlyAt") }}: {{ completedEarlyAtFormatted }}
        </span>
      </div>
    </template>

    <DeletePromotionModal
      :show.sync="isShownDeleteModal"
      :promotion-id="promotionId"
      :name="form.name"
      @confirm="onConfirmDelete"
    />

    <EndEarlyConfirmModal
      :show.sync="isShownEndEarlyModal"
      :promotion-id="promotionId"
      :name="form.name"
      @endedEarly="onEndedEarly"
    />

    <PromotionsForm
      ref="promotionsForm"
      v-model="form"
      :upload-files="uploadFiles"
      :type="fileUploadType"
      @uploadFiles="handleUploadFiles"
      @deleteFiles="handleDeleteFile"
    />

    <PromotionProductsSection
      ref="productsSection"
      v-model="products"
      :dealer-incentive-id="promotionId"
      :products-pagination-data="productsPaginatorInfo"
      :products-page-loading="productsPageLoading"
      :readonly="isProductsReadonly"
      :not-found-skus.sync="notFoundSkus"
      :duplicate-skus.sync="duplicateSkus"
      @pageChange="onProductsPageChange"
    />

    <template #footer-left>
      <OurButton
        v-if="getPermission['dealer_incentive.update']"
        class="button"
        :text="$t('button.save')"
        @click="onClickSaveButton"
      />
      <OurLink :route="backRoute">
        <OurButton class="button" :text="$t('button.back')" variant="secondary" />
      </OurLink>
    </template>

    <template #footer-right>
      <OurButton
        v-if="canEndEarly && getPermission['dealer_incentive.update']"
        class="button"
        variant="thirdary"
        :text="$t('promotions.endEarly')"
        @click="onClickEndEarlyButton"
      />

      <OurButton
        v-if="getPermission['dealer_incentive.delete']"
        class="button"
        variant="thirdary"
        :text="$t('button.remove')"
        @click="onClickDeleteButton"
      />
    </template>
  </OurPage>
</template>

<script>
import { mapGetters } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import DateService from "@/services/date.service";
import PromotionsForm from "@/views/settings/promotions/add/_components/PromotionsForm.vue";
import PromotionProductsSection from "@/views/settings/promotions/add/_components/PromotionProductsSection.vue";
import DeletePromotionModal from "@/views/settings/promotions/edit/_components/DeletePromotionModal.vue";
import EndEarlyConfirmModal from "@/views/settings/promotions/edit/_components/EndEarlyConfirmModal.vue";
import {
  deleteFileMutation,
  uploadFilesMutation,
} from "@/views/settings/base-knowledge/chapter/add/gql/mutations";
import { dealerIncentiveQuery } from "@/views/settings/promotions/edit/gql/queries";
import {
  deleteDealerIncentiveMutation,
  updateDealerIncentiveMutation,
} from "@/views/settings/promotions/edit/gql/mutations";
import { DEFAULT_MEASUREMENT_UNIT } from "@/views/settings/promotions/add/constants";

const DEFAULT_PRODUCTS_PER_PAGE = 50;
const END_EARLY_STATUSES = ["planned", "active"];
const READONLY_PRODUCT_STATUSES = ["completed", "completed_early"];

export default {
  name: "PromotionsEdit",

  components: {
    PromotionsForm,
    PromotionProductsSection,
    DeletePromotionModal,
    EndEarlyConfirmModal,
  },

  mixins: [apolloMixin],

  data() {
    return {
      backRoute: {
        name: "Promotions",
        title: this.$t("title.promotions._"),
      },
      form: {
        name: "",
        description: "",
        startsAt: null,
        endsAt: null,
        measurementUnit: DEFAULT_MEASUREMENT_UNIT,
        targetValue: "",
        reward: "",
        color: "#3B82F6",
        typeId: "",
        files: { sync: [] },
      },
      products: [],
      productsPaginatorInfo: {},
      productsPerPage: DEFAULT_PRODUCTS_PER_PAGE,
      productsPageLoading: false,
      notFoundSkus: [],
      duplicateSkus: [],
      uploadFiles: [],
      fileUploadType: "",
      status: "",
      completedEarlyAt: null,
      createdAt: null,
      creator: null,
      isShownDeleteModal: false,
      isShownEndEarlyModal: false,
    };
  },

  computed: {
    ...mapGetters("user", ["getPermission"]),
    ...mapGetters("promotions", ["getStatusByValue"]),

    promotionId() {
      return this.$route.params.id;
    },

    statusTag() {
      return this.getStatusByValue(this.status);
    },

    completedEarlyAtFormatted() {
      if (!this.completedEarlyAt) {
        return "";
      }

      return DateService.dateConverter(
        parseInt(this.completedEarlyAt, 10),
        "dateWithFullMonthAndTime"
      );
    },

    createdAtFormatted() {
      if (!this.createdAt) {
        return "";
      }

      return DateService.dateConverter(parseInt(this.createdAt, 10), "dateWithFullMonthAndTime");
    },

    creatorName() {
      return this.formatCreatorName(this.creator);
    },

    canEndEarly() {
      return END_EARLY_STATUSES.includes(this.status?.toLowerCase());
    },

    isProductsReadonly() {
      return READONLY_PRODUCT_STATUSES.includes(this.status?.toLowerCase());
    },
  },

  created() {
    this.getData();
  },

  methods: {
    goToList() {
      this.$router.push({ name: "Promotions" });
    },

    formatCreatorName(creator) {
      if (!creator) {
        return "";
      }

      const name = [];

      if (creator.firstName) {
        name.push(creator.firstName);
      }

      if (creator.secondName) {
        name.push(creator.secondName);
      }

      if (creator.lastName) {
        name.push(creator.lastName);
      }

      return name.join(" ") || this.$t("label.createdByAtmosphere");
    },

    onClickDeleteButton() {
      this.isShownDeleteModal = true;
    },

    async onConfirmDelete() {
      const { deleteDealerIncentive } = await this.$post(
        deleteDealerIncentiveMutation,
        { id: this.promotionId },
        { delaySuccessNotify: true }
      );

      if (deleteDealerIncentive?.id) {
        this.isShownDeleteModal = false;
        await this.goToList();
      }
    },

    onClickEndEarlyButton() {
      this.isShownEndEarlyModal = true;
    },

    async onEndedEarly(result) {
      this.status = result.status;
      this.completedEarlyAt = result.completedEarlyAt;
      this.isShownEndEarlyModal = false;
      await this.goToList();
    },

    mapProduct(product) {
      return {
        id: product.id,
        productCode: product.productCode,
        name: product.name,
        categoryName: product.category?.description || "–",
      };
    },

    async loadProductsPage(page, perPage = this.productsPerPage) {
      this.productsPageLoading = true;

      try {
        const { dealerIncentive } = await this.$get(
          dealerIncentiveQuery,
          {
            id: this.promotionId,
            productsPage: page,
            productsPerPage: perPage,
          },
          { withLoader: false, withNotify: false }
        );

        this.products = (dealerIncentive?.products?.data || []).map((product) =>
          this.mapProduct(product)
        );
        this.productsPaginatorInfo = dealerIncentive?.products?.paginatorInfo || {};
        this.productsPerPage = perPage;
      } finally {
        this.productsPageLoading = false;
      }
    },

    onProductsPageChange({ page, perPage }) {
      this.loadProductsPage(page, perPage);
    },

    async getData() {
      const { dealerIncentive } = await this.$get(dealerIncentiveQuery, {
        id: this.promotionId,
        productsPage: 1,
        productsPerPage: this.productsPerPage,
      });

      if (!dealerIncentive) {
        return;
      }

      const {
        name,
        description,
        startsAt,
        endsAt,
        measurementUnit,
        targetValue,
        reward,
        color,
        status,
        completedEarlyAt,
        createdAt,
        creator,
        type,
        banner,
        icon,
        products,
      } = dealerIncentive;

      this.form = {
        name,
        description,
        startsAt: startsAt ? parseInt(startsAt, 10) : null,
        endsAt: endsAt ? parseInt(endsAt, 10) : null,
        measurementUnit: measurementUnit || DEFAULT_MEASUREMENT_UNIT,
        targetValue: targetValue ?? "",
        reward: reward || "",
        color: color || "#3B82F6",
        typeId: type?.id || "",
        type,
        banner,
        icon,
        files: { sync: [] },
      };

      this.status = status;
      this.completedEarlyAt = completedEarlyAt;
      this.createdAt = createdAt;
      this.creator = creator;

      this.products = (products?.data || []).map((product) => this.mapProduct(product));
      this.productsPaginatorInfo = products?.paginatorInfo || {};
    },

    async handleUploadFiles({ selectedFiles, type }) {
      const { uploadFiles: files } = await this.$post(
        uploadFilesMutation,
        { files: selectedFiles, collection: type },
        { withLoader: false, withNotify: false }
      );

      this.uploadFiles = files || [];
      this.fileUploadType = type;
    },

    async handleDeleteFile(id) {
      await this.$post(deleteFileMutation, { id }, { withLoader: false, withNotify: false });
    },

    async onClickSaveButton() {
      if (!this.$refs.promotionsForm.emitForm()) {
        return;
      }

      if (!this.$refs.productsSection.validate()) {
        return;
      }

      const {
        name,
        description,
        startsAt,
        endsAt,
        measurementUnit,
        targetValue,
        reward,
        color,
        typeId,
        files,
      } = this.form;

      const { updateDealerIncentive } = await this.$post(
        updateDealerIncentiveMutation,
        {
          id: this.promotionId,
          name: name.trim(),
          description,
          startsAt,
          endsAt,
          measurementUnit,
          targetValue,
          reward: reward.trim(),
          color,
          typeId,
          files,
        },
        { delaySuccessNotify: true }
      );

      if (!updateDealerIncentive?.id) {
        return;
      }

      await this.goToList();
    },
  },
};
</script>
